import React, { useState, useEffect } from 'react';
import { Bell, Send, Users, Smartphone, History, Loader2, RefreshCw } from 'lucide-react';
import api from '../../services/api';

const NotificationManagement = () => {
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [notification, setNotification] = useState({
    title: '',
    body: '',
    send_to: 'all'
  });
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, historyRes] = await Promise.all([
        api.get('/notifications/stats'),
        api.get('/notifications/history?limit=10')
      ]);
      setStats(statsRes.data);
      setHistory(historyRes.data);
    } catch (error) {
      console.error('Error loading notification data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();
    if (!notification.title || !notification.body) {
      setMessage({ type: 'error', text: 'Title and body are required' });
      return;
    }

    setSending(true);
    setMessage(null);
    try {
      const response = await api.post('/notifications/send-promotional', notification);
      setMessage({ 
        type: 'success', 
        text: `Notification sent to ${response.data.target_count} devices!` 
      });
      setNotification({ title: '', body: '', send_to: 'all' });
      loadData(); // Refresh stats and history
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to send notification' 
      });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 text-electric animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white/5 rounded-xl p-4 border border-white/10">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-electric/20 rounded-lg">
              <Users className="h-5 w-5 text-electric" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{stats?.active_tokens || 0}</p>
              <p className="text-xs text-muted-foreground">Active Devices</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white/5 rounded-xl p-4 border border-white/10">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <Smartphone className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{stats?.ios_tokens || 0}</p>
              <p className="text-xs text-muted-foreground">iOS Devices</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white/5 rounded-xl p-4 border border-white/10">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <Smartphone className="h-5 w-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{stats?.android_tokens || 0}</p>
              <p className="text-xs text-muted-foreground">Android Devices</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white/5 rounded-xl p-4 border border-white/10">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <Bell className="h-5 w-5 text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{stats?.total_notifications_sent || 0}</p>
              <p className="text-xs text-muted-foreground">Total Sent</p>
            </div>
          </div>
        </div>
      </div>

      {/* Send Notification Form */}
      <div className="bg-white/5 rounded-xl border border-white/10 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
            <Send className="h-5 w-5 text-electric" />
            <span>Send Push Notification</span>
          </h3>
          <button
            onClick={loadData}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>

        {message && (
          <div className={`mb-4 p-3 rounded-lg ${
            message.type === 'success' 
              ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
              : 'bg-red-500/20 text-red-400 border border-red-500/30'
          }`}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSendNotification} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-2">
              Notification Title *
            </label>
            <input
              type="text"
              value={notification.title}
              onChange={(e) => setNotification(prev => ({ ...prev, title: e.target.value }))}
              placeholder="e.g., New Content Available!"
              className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric text-foreground"
              maxLength={100}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-2">
              Message Body *
            </label>
            <textarea
              value={notification.body}
              onChange={(e) => setNotification(prev => ({ ...prev, body: e.target.value }))}
              placeholder="e.g., Check out our latest movie releases!"
              rows={3}
              className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric text-foreground resize-none"
              maxLength={500}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-2">
              Send To
            </label>
            <select
              value={notification.send_to}
              onChange={(e) => setNotification(prev => ({ ...prev, send_to: e.target.value }))}
              className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric text-foreground"
            >
              <option value="all">All Devices ({stats?.active_tokens || 0})</option>
            </select>
          </div>

          <button
            type="submit"
            disabled={sending || !notification.title || !notification.body}
            className="flex items-center justify-center space-x-2 w-full px-6 py-3 bg-electric text-background rounded-xl font-semibold hover:bg-electric/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {sending ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Send className="h-5 w-5" />
            )}
            <span>{sending ? 'Sending...' : 'Send Notification'}</span>
          </button>
        </form>
      </div>

      {/* Notification History */}
      <div className="bg-white/5 rounded-xl border border-white/10 p-6">
        <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2 mb-4">
          <History className="h-5 w-5 text-electric" />
          <span>Recent Notifications</span>
        </h3>

        {history.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No notifications sent yet
          </div>
        ) : (
          <div className="space-y-3">
            {history.map((item, index) => (
              <div
                key={index}
                className="flex items-start justify-between p-3 bg-white/5 rounded-lg"
              >
                <div className="flex-1">
                  <p className="font-medium text-foreground">{item.title}</p>
                  <p className="text-sm text-muted-foreground line-clamp-1">{item.body}</p>
                  <div className="flex items-center space-x-3 mt-1">
                    <span className="text-xs text-muted-foreground">
                      {new Date(item.sent_at).toLocaleDateString()}
                    </span>
                    <span className="text-xs px-2 py-0.5 bg-electric/20 text-electric rounded">
                      {item.type}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-sm font-medium text-foreground">{item.tokens_count}</span>
                  <p className="text-xs text-muted-foreground">devices</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationManagement;
